import { useState } from 'react';
import type { Target } from '../types';

export interface HideRequest {
  target: Target;
  reason: string;
  /** ISO date, or null for indefinite. */
  until: string | null;
}

/**
 * Hiding asks for a reason and an optional expiry.
 *
 * The reason is not bureaucracy: a hidden machine is a backup nobody is
 * watching, and in six months "why is this hidden?" needs an answer that is not
 * a guess. The expiry exists because most hiding is temporary — a device being
 * rebuilt, a client mid-migration — and a snooze that lapses on its own is far
 * safer than one somebody has to remember to undo.
 */
export function HideDialog({ target, onCancel, onConfirm }: {
  target: Target;
  onCancel: () => void;
  onConfirm: (req: HideRequest) => void;
}) {
  const [reason, setReason] = useState('');
  const [mode, setMode] = useState<'forever' | '7' | '30' | 'date'>('30');
  const [date, setDate] = useState('');

  const until = (() => {
    if (mode === 'forever') return null;
    if (mode === 'date') return date ? new Date(date + 'T12:00:00').toISOString() : null;
    const d = new Date();
    d.setDate(d.getDate() + Number(mode));
    return d.toISOString();
  })();

  return (
    <div className="backdrop open" onClick={onCancel}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-hd">
          <div className="modal-ttl">Hide {target.name}</div>
          <button className="modal-x" onClick={onCancel}>&times;</button>
        </div>

        <div className="modal-bd">
          <p className="panel-note" style={{ marginBottom: 0 }}>
            It disappears from the table and from every count for everyone in this organisation.
            It keeps syncing in the background, so it is current the moment it comes back.
          </p>

          <div className="fg">
            <div className="flbl">Reason</div>
            <input
              className="finput" value={reason} autoFocus
              placeholder="Being rebuilt / decommissioned / client cancelled"
              onChange={(e) => setReason(e.target.value)}
            />
          </div>

          <div className="fg">
            <div className="flbl">Bring it back</div>
            <div className="sts-row">
              {([['7', 'in 7 days'], ['30', 'in 30 days'], ['date', 'on a date'], ['forever', 'never']] as const)
                .map(([v, label]) => (
                  <button
                    key={v} type="button"
                    className={'sts-opt' + (mode === v ? ' sel' : '')}
                    onClick={() => setMode(v)}
                  >
                    {label}
                  </button>
                ))}
            </div>
          </div>

          {mode === 'date' && (
            <div className="fg">
              <div className="flbl">Date</div>
              <input className="finput" type="date" value={date}
                     min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)}
                     onChange={(e) => setDate(e.target.value)} />
            </div>
          )}

          {mode === 'forever' && (
            <p className="panel-note" style={{ color: 'var(--yellow)' }}>
              Nothing will bring this back but someone unhiding it in Settings. If the machine is
              only temporarily out of service, pick a date instead.
            </p>
          )}
        </div>

        <div className="modal-ft">
          <button className="btn" onClick={onCancel}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={mode === 'date' && !date}
            onClick={() => onConfirm({ target, reason: reason.trim(), until })}
          >
            Hide
          </button>
        </div>
      </div>
    </div>
  );
}
